import JSZip from 'jszip';

import { useDashboardBulkExportStore } from '../useDashboardBulkExportStore';

/**
 * @returns Function to generate and download a zip file of all widget exports
 */
export const useGetZip = () => {
  const { widgetExports } = useDashboardBulkExportStore();

  const getZip = async () => {
    const zip = new JSZip();

    // Track used base names so widgets sharing a title (or all untitled) don't
    // collide on the same zip entry — subsequent duplicates get a numeric suffix.
    const usedNames = new Map<string, number>();

    // Names are deduped per extension so a chart PNG and a table CSV sharing a
    // base title don't collide, while two same-named PNGs still get suffixed.
    const dedupedName = (base: string, ext: string): string => {
      const key = `${base}.${ext}`;
      const seen = usedNames.get(key) ?? 0;
      usedNames.set(key, seen + 1);

      return seen === 0 ? base : `${base}_${seen + 1}`;
    };

    for (const we of widgetExports) {
      const content = await we.exportFn();
      if (content === undefined) {
        continue;
      }
      const base = we.filename || we.id;

      if (content.kind === 'image') {
        const blob = await new Promise<Blob | null>((resolve) =>
          content.canvas.toBlob(resolve)
        );
        if (blob) {
          zip.file(`${dedupedName(base, 'png')}.png`, blob);
        }
      } else if (content.csv) {
        // Skip empty CSVs (e.g. a failed/aborted fetch returned ''). Prefix the
        // UTF-8 BOM so Excel renders non-ASCII characters correctly, matching
        // the standalone custom-datasource CSV export.
        zip.file(`${dedupedName(base, 'csv')}.csv`, `\ufeff${content.csv}`);
      }
    }

    const fullZip = await zip.generateAsync({ type: 'blob' });

    // Download the zip file
    const link = document.createElement('a');
    link.href = URL.createObjectURL(fullZip);
    link.download = `dashboard_export.zip`;
    link.click();
  };

  return { getZip };
};
